import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import { formatDateLocal } from '../../utils/dateUtils';
import { apiRequest } from '../../utils/api';

interface User {
  id: number;
  username: string;
  email: string;
}

interface AttendanceEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  checkin: string | null;
  checkout: string | null;
  in_position: string | null;
  out_position: string | null;
}

interface AttendanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    user_id: number;
    date: string;
    checkin: string | null;
    checkout: string | null;
    in_position?: string | null;
    out_position?: string | null;
    record_id?: number | null;
  }) => void;
  users: User[];
  entry: AttendanceEntry | null;
}

export default function AttendanceDialog({
  open,
  onClose,
  onSave,
  users,
  entry,
}: AttendanceDialogProps) {
  const [userId, setUserId] = useState<number | ''>('');
  const [date, setDate] = useState<string>('');
  const [checkin, setCheckin] = useState<string>('');
  const [checkout, setCheckout] = useState<string>('');
  const [todayRecords, setTodayRecords] = useState<AttendanceEntry[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);

  // Load today's attendance records when user/date changes
  useEffect(() => {
    if (open && userId && date) {
      loadTodayRecords();
    } else {
      setTodayRecords([]);
    }
  }, [open, userId, date]);

  // Initialize form when entry changes
  useEffect(() => {
    if (entry) {
      setUserId(entry.user_id);
      setDate(entry.date || '');
      setEditingRecordId(entry.id);
      // Extract time portion (HH:mm) from datetime
      if (entry.checkin) {
        const checkinDate = new Date(entry.checkin);
        const hours = String(checkinDate.getHours()).padStart(2, '0');
        const minutes = String(checkinDate.getMinutes()).padStart(2, '0');
        setCheckin(`${hours}:${minutes}`);
      } else {
        setCheckin('');
      }
      if (entry.checkout) {
        const checkoutDate = new Date(entry.checkout);
        const hours = String(checkoutDate.getHours()).padStart(2, '0');
        const minutes = String(checkoutDate.getMinutes()).padStart(2, '0');
        setCheckout(`${hours}:${minutes}`);
      } else {
        setCheckout('');
      }
    } else {
      setUserId('');
      setDate(formatDateLocal(new Date()));
      setCheckin('');
      setCheckout('');
      setEditingRecordId(null);
    }
  }, [entry, open]);

  const loadTodayRecords = async () => {
    if (!userId || !date) return;
    
    setLoadingRecords(true);
    try {
      // Use ungrouped=true to get all individual records, not grouped ones
      const response = await apiRequest(
        `/working-log/attendance?user_id=${userId}&date_from=${date}&date_to=${date}&ungrouped=true`,
        { method: 'GET' }
      );
      const result = response.data;
      
      let data: any[] = [];
      if (Array.isArray(result)) {
        data = result;
      } else {
        data = result?.data || [];
      }

      // Filter to match the exact date (ungrouped endpoint already filters by log_type)
      const filtered = data
        .filter((item: any) => {
          // Ensure date matches exactly
          return item.date === date;
        })
        .map((item: any) => ({
          id: item.id,
          user_id: item.user_id,
          user_name: item.user_name || 'Unknown',
          user_email: item.user_email || '',
          date: item.date,
          checkin: item.checkin,
          checkout: item.checkout,
          in_position: item.in_position,
          out_position: item.out_position,
        }));

      setTodayRecords(filtered);
    } catch (error) {
      console.error('Error loading today\'s attendance records:', error);
      setTodayRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleSelectRecord = (record: AttendanceEntry) => {
    setEditingRecordId(record.id);
    // Extract time portion (HH:mm) from datetime
    if (record.checkin) {
      const checkinDate = new Date(record.checkin);
      const hours = String(checkinDate.getHours()).padStart(2, '0');
      const minutes = String(checkinDate.getMinutes()).padStart(2, '0');
      setCheckin(`${hours}:${minutes}`);
    } else {
      setCheckin('');
    }
    if (record.checkout) {
      const checkoutDate = new Date(record.checkout);
      const hours = String(checkoutDate.getHours()).padStart(2, '0');
      const minutes = String(checkoutDate.getMinutes()).padStart(2, '0');
      setCheckout(`${hours}:${minutes}`);
    } else {
      setCheckout('');
    }
  };

  const handleCreateNew = () => {
    setEditingRecordId(null);
    setCheckin('');
    setCheckout('');
  };

  const handleSave = () => {
    if (!userId || !date) {
      alert('Please select a user and date');
      return;
    }

    // Combine date with time to create full datetime
    // When editing, we need to be careful about timezone conversions
    // The backend will derive the date from checkin/checkout times, so we just need to
    // construct the datetime strings correctly
    let checkinDateTime = null;
    if (checkin) {
      const [hours, minutes] = checkin.split(':');
      // Create date in local timezone to avoid UTC conversion issues
      // Parse the date string as local date (YYYY-MM-DD format)
      const [year, month, day] = date.split('-').map(Number);
      const checkinDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      checkinDateTime = checkinDate.toISOString();
    }

    let checkoutDateTime = null;
    if (checkout) {
      const [hours, minutes] = checkout.split(':');
      // Use the same date as checkin (or the date field if no checkin)
      // If checkin exists, derive date from checkin to ensure consistency
      let checkoutDateBase = date;
      if (checkin && editingRecordId) {
        // When editing and checkin exists, use the date from checkin datetime
        const checkinDateObj = new Date(checkinDateTime!);
        const year = checkinDateObj.getFullYear();
        const month = String(checkinDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(checkinDateObj.getDate()).padStart(2, '0');
        checkoutDateBase = `${year}-${month}-${day}`;
      }
      const [year, month, day] = checkoutDateBase.split('-').map(Number);
      const checkoutDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      checkoutDateTime = checkoutDate.toISOString();
    }

    // For editing, the backend will derive the date from checkin/checkout times
    // So we don't need to send the date field when updating
    onSave({
      user_id: userId as number,
      date,
      checkin: checkinDateTime,
      checkout: checkoutDateTime,
      record_id: editingRecordId,
    });
    
    // Reload records after save
    if (userId && date) {
      setTimeout(() => {
        loadTodayRecords();
      }, 500);
    }
  };

  const formatTime = (datetime: string | null) => {
    if (!datetime) return 'N/A';
    const date = new Date(datetime);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {entry ? 'Edit Attendance Record' : 'Create Attendance Record'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                label="User"
                onChange={(e) => setUserId(e.target.value as number)}
                disabled={!!entry}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              disabled={!!entry}
              fullWidth
            />
          </Box>

          {/* Attendance Records for Selected Date */}
          {userId && date && (
            <>
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Attendance Records for {date} ({todayRecords.length})
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={handleCreateNew}
                    variant="outlined"
                    size="small"
                  >
                    New Record
                  </Button>
                </Box>
                {loadingRecords ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading records...
                  </Typography>
                ) : todayRecords.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No attendance records found for this date.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Check In</TableCell>
                          <TableCell>Check Out</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {todayRecords.map((record) => (
                          <TableRow
                            key={record.id}
                            sx={{
                              backgroundColor: editingRecordId === record.id ? 'action.selected' : 'inherit',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell>{formatTime(record.checkin)}</TableCell>
                            <TableCell>{formatTime(record.checkout)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleSelectRecord(record)}
                                color={editingRecordId === record.id ? 'primary' : 'default'}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              <Divider />
            </>
          )}

          {/* Edit Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {editingRecordId ? `Editing Record #${editingRecordId}` : 'Create New Record'}
            </Typography>
            <TextField
              label="Check In Time"
              type="time"
              value={checkin}
              onChange={(e) => setCheckin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Check Out Time"
              type="time"
              value={checkout}
              onChange={(e) => setCheckout(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!userId || !date}>
          {editingRecordId ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
